/**
 * AudioManager — independent music + sfx buses with iOS unlock.
 *
 * iOS Safari refuses to play any audio until a user gesture has resumed
 * the AudioContext. We attach a one-time pointer/key listener to do that
 * automatically the first time the player interacts with the page.
 *
 * iOS audio session category trap: every page belongs to an audio session
 * "category" that iOS picks at first-play time. The hardware silent switch
 * silences the **ambient** category but ignores **playback**. If the silent
 * switch is ON when our AudioContext first plays, iOS tags us "ambient" —
 * and *toggling the switch off does NOT re-route us*. The user hears
 * nothing even though every JS-side flag is green. We mitigate with two
 * additive layers:
 *   1. `navigator.audioSession.type = 'playback'` (iOS 17+ official API)
 *   2. A silent <audio playsInline> played on the first user gesture, to
 *      register the page as a media-playback origin on older iOS (12-16).
 * Plus a one-shot suspend()+resume() pump inside `_forceUnlock` to flush
 * any cached "ambient" routing on a context that was already running.
 *
 * Volume buses:
 *   - musicVolume — long looping tracks (one-at-a-time)
 *   - sfxVolume   — short one-shots
 *
 * Volumes are persisted via Storage so they survive reloads.
 */
import { platformStorage } from '../save/Storage.js';

// 44-byte minimal silent WAV (mono, 8kHz, 8-bit, 0 samples). Used by the
// silent unlocker to register the page as a media-playback origin so iOS
// routes Web Audio through the playback session category. Inline so the
// SDK has zero network dependencies.
const SILENT_WAV_DATA_URI =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAVFYAAFRWAAABAAgAZGF0YQAAAAA=';

export class AudioManager {
  /**
   * @param {object} opts
   * @param {Phaser.Game} opts.game
   */
  constructor(opts) {
    this.game = opts.game;
    this.unlocked = false;

    this.musicVolume = platformStorage.get('musicVolume', 0.6);
    this.sfxVolume = platformStorage.get('sfxVolume', 0.8);
    this.muted = platformStorage.get('muted', false);

    this._currentMusic = null;

    // Diagnostic trail of every unlock-related event the AudioManager
    // observes, in order. Read by `games/audio-diagnostics` to verify
    // first-tap unlock on real iOS / Android devices. Each entry is
    // `{ ts, kind, ... }` where `kind` is one of:
    //   - 'first-event' — first user gesture seen by the unlock listener
    //   - 'force-unlock' — _forceUnlock() ran (with before/after state)
    //   - 'session-set' — set navigator.audioSession.type
    //   - 'silent-play' — played the silent <audio> element
    this._unlockHistory = [];

    this._silentEl = null;
    this._installAudioSession();
    this._installUnlockListener();
  }

  /**
   * iOS audio session category fix — set up before the first AudioContext
   * play so iOS classifies our page as media playback (not ambient).
   * Both layers are no-ops on platforms that don't need them.
   */
  _installAudioSession() {
    // Layer 1 — iOS 17+ official Web Audio Session API.
    if (typeof navigator !== 'undefined' && navigator.audioSession) {
      try {
        navigator.audioSession.type = 'playback';
        this._unlockHistory.push({
          ts: nowMs(),
          kind: 'session-set',
          type: 'playback',
          via: 'navigator.audioSession'
        });
      } catch (_) { /* older Safari — fall through to layer 2 */ }
    }
    // Layer 2 — silent media element. Created now, played on first
    // gesture by `_installUnlockListener`. Targets iOS 12-16 where the
    // audioSession API isn't available. `playsInline` is what tells iOS
    // this is media playback rather than a ringtone-style sound.
    if (typeof Audio !== 'undefined') {
      try {
        const el = new Audio(SILENT_WAV_DATA_URI);
        el.playsInline = true;
        el.preload = 'auto';
        el.loop = false;
        el.volume = 0;
        this._silentEl = el;
      } catch (_) { /* ignore — node tests, older browsers */ }
    }
  }

  _installUnlockListener() {
    if (this.unlocked) return;
    if (typeof window === 'undefined') return;

    const events = ['pointerdown', 'touchstart', 'touchend', 'click', 'keydown'];
    // Listen on window AND document AND the game canvas. Phaser's
    // canvas input swallows events before they reach `window` in some
    // Phaser / iOS combinations, so we hook the canvas itself to make
    // sure every tap triggers the unlock path at least once.
    const targets = [];
    targets.push(window);
    if (typeof document !== 'undefined') targets.push(document);
    if (this.game && this.game.canvas) targets.push(this.game.canvas);

    const unlock = (ev) => {
      // Record the very first user gesture the unlock listener observed,
      // along with the AudioContext state at that exact moment. This is
      // the "smoking gun" for first-tap unlock issues on iOS / Android —
      // if the diagnostic page later finds no 'first-event' entry, the
      // listener never fired; if it finds one but ctx is still suspended
      // afterwards, _forceUnlock failed to do its job.
      const isFirst = !this.unlocked;
      if (isFirst) {
        const snd = this.game && this.game.sound;
        const ctx = snd && snd.context;
        this._unlockHistory.push({
          ts: nowMs(),
          kind: 'first-event',
          type: ev && ev.type,
          target: ev && ev.currentTarget && (ev.currentTarget === window
            ? 'window'
            : (ev.currentTarget === document ? 'document' : 'canvas')),
          ctxState: ctx && ctx.state,
          sndLocked: !!(snd && snd.locked)
        });
        // Layer 2 — kick the silent <audio> inside the gesture so iOS
        // registers the page as a media-playback origin and routes
        // future Web Audio through the playback session category. The
        // .play() promise rejects on browsers that don't allow media
        // autoplay even from a gesture (rare); ignore the failure since
        // layer 1 (audioSession API) usually has us covered already.
        if (this._silentEl) {
          try {
            const p = this._silentEl.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
            this._unlockHistory.push({ ts: nowMs(), kind: 'silent-play' });
          } catch (_) { /* ignore */ }
        }
      }
      this._forceUnlock();
      // Replay any music that was requested while the context was still
      // locked. iOS rejects play() silently when suspended, so we stash
      // the intended key in _pendingMusic and retry here.
      if (this._pendingMusic) {
        const key = this._pendingMusic;
        this._pendingMusic = null;
        this.playMusic(key);
      }
      for (const t of targets) {
        for (const ev of events) {
          try { t.removeEventListener(ev, unlock); } catch (_) { /* ignore */ }
        }
      }
    };

    for (const t of targets) {
      for (const ev of events) {
        try { t.addEventListener(ev, unlock, { passive: true }); } catch (_) { /* ignore */ }
      }
    }
  }

  /**
   * Fully unlock the audio pipeline. Three things can block sound on a
   * mobile browser and we hit all of them:
   *   1. Web Audio context is suspended (iOS + Chrome) — resume it
   *   2. Phaser's sound manager is "locked" — call its built-in unlock
   *   3. Phaser's `locked` flag lingers even after unlock — clear it
   */
  _forceUnlock() {
    const isFirst = !this.unlocked;
    const snd = this.game && this.game.sound;
    const ctx = snd && snd.context;
    const before = {
      ctxState: ctx && ctx.state,
      sndLocked: !!(snd && snd.locked),
      sdkUnlocked: this.unlocked
    };
    if (snd) {
      if (ctx && ctx.state === 'suspended') {
        try { ctx.resume(); } catch (_) { /* ignore */ }
      }
      if (typeof snd.unlock === 'function') {
        try { snd.unlock(); } catch (_) { /* ignore */ }
      }
      if (snd.locked) snd.locked = false;
    }
    // iOS quirk: even after `ctx.resume()`, audio may stay routed through
    // the silent-switch-controlled "ambient" session category if the
    // AudioContext was first reached while the phone was on mute. A
    // suspend()/resume() pair on the *first* unlock forces iOS to release
    // and re-acquire the audio session, picking up the playback category
    // we set in `_installAudioSession`. Both calls go in synchronously
    // so they stay inside the gesture window iOS requires. Subsequent
    // unlocks skip this to avoid jitter.
    if (isFirst && ctx && typeof ctx.suspend === 'function' && typeof ctx.resume === 'function') {
      try { ctx.suspend(); } catch (_) { /* ignore */ }
      try { ctx.resume(); } catch (_) { /* ignore */ }
    }
    this.unlocked = true;
    this._unlockHistory.push({
      ts: nowMs(),
      kind: 'force-unlock',
      before,
      after: {
        ctxState: ctx && ctx.state,
        sndLocked: !!(snd && snd.locked),
        sdkUnlocked: this.unlocked
      }
    });
  }

  /** Play a long looping track. Stops any other music first. */
  playMusic(key) {
    if (this.muted) return;
    if (this._currentMusic && this._currentMusic.key === key && this._currentMusic.isPlaying) return;
    // If the audio pipeline is still locked (no user gesture yet) on a
    // mobile browser, stash the intended key and let the unlock listener
    // retry once the context resumes.
    if (this._isLocked()) {
      this._pendingMusic = key;
      return;
    }
    this.stopMusic();
    if (!this.game.cache.audio.exists(key)) return;
    try {
      const sound = this.game.sound.add(key, { loop: true, volume: this.musicVolume });
      const p = sound.play();
      // Some browsers return a Promise from play(); if it rejects
      // (usually because the context is still suspended), remember the
      // key and retry on the next user gesture.
      if (p && typeof p.catch === 'function') {
        p.catch(() => { this._pendingMusic = key; });
      }
      this._currentMusic = sound;
    } catch (_) {
      this._pendingMusic = key;
    }
  }

  stopMusic() {
    this._pendingMusic = null;
    if (!this._currentMusic) return;
    try { this._currentMusic.stop(); this._currentMusic.destroy(); } catch (_) { /* ignore */ }
    this._currentMusic = null;
  }

  /** Fire a one-shot SFX. */
  playSfx(key, { volume } = {}) {
    if (this.muted) return;
    if (!this.game.cache.audio.exists(key)) return;
    // On a locked audio pipeline, try to unlock opportunistically
    // (we may be inside a user-gesture callback) then play.
    if (this._isLocked()) this._forceUnlock();
    const v = (volume == null ? 1 : volume) * this.sfxVolume;
    try {
      this.game.sound.play(key, { volume: v });
    } catch (_) { /* ignore — device may still be locked */ }
  }

  /** True when Phaser sound OR the underlying Web Audio context is locked. */
  _isLocked() {
    const snd = this.game && this.game.sound;
    if (!snd) return false;
    if (snd.locked) return true;
    const ctx = snd.context;
    if (ctx && ctx.state === 'suspended') return true;
    return false;
  }

  setMusicVolume(v) {
    this.musicVolume = clamp01(v);
    platformStorage.set('musicVolume', this.musicVolume);
    if (this._currentMusic) this._currentMusic.setVolume(this.musicVolume);
  }

  setSfxVolume(v) {
    this.sfxVolume = clamp01(v);
    platformStorage.set('sfxVolume', this.sfxVolume);
  }

  setMuted(muted) {
    this.muted = !!muted;
    platformStorage.set('muted', this.muted);
    if (this.muted) this.stopMusic();
  }
}

function clamp01(v) {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function nowMs() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}
